import { useState } from "react";
import { KpiCard } from "../../components/admin/AdminUi";
import { DataTable } from "../../components/admin/DataTable";
import { Banner } from "../../components/Banner";
import { FileButton } from "../../components/FileButton";
import { parseRoster } from "../../lib/roster";

interface RosterScreenProps {
  size: number;
  busy: boolean;
  message: string | null;
  error: string | null;
  onImport: (entries: { email: string; participantId?: string }[], replace: boolean) => void;
}

type Entry = { email: string; participantId?: string };

/** Load the GATEWAY roster from a CSV, with a preview of what will be loaded before anything changes. */
export function RosterScreen({ size, busy, message, error, onImport }: RosterScreenProps) {
  const [entries, setEntries] = useState<Entry[]>([]);
  const [replace, setReplace] = useState(false);
  const [fileName, setFileName] = useState("");

  const read = async (file: File) => {
    setFileName(file.name);
    setEntries(parseRoster(await file.text()));
  };
  const withoutId = entries.filter((e) => !e.participantId).length;

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <KpiCard
          label="On the roster"
          value={size}
          tone={size ? "positive" : "warning"}
          hint={
            size
              ? "Only these emails can sign in"
              : "No roster: the first person to sign in with an email claims it"
          }
        />
        <div className="rounded-[14px] border border-border bg-surface p-4 text-[13.5px]">
          <b className="block">CSV format</b>
          <code className="mt-1 block rounded-[8px] bg-surface-high px-2 py-1 text-[12.5px]">
            email,participant_id
          </code>
          <p className="m-0 mt-1.5 text-text-muted">
            Comma, semicolon or tab. The header row is skipped. A blank ID accepts any ID.
          </p>
        </div>
      </div>

      <div className="rounded-[14px] border border-border bg-surface p-5">
        <div className="flex flex-wrap items-center gap-3">
          <FileButton
            label="Choose roster CSV"
            ariaLabel="Roster CSV"
            accept=".csv,text/csv,text/plain"
            hasFile={Boolean(fileName)}
            onFile={(file) => void read(file)}
          />
          {fileName ? (
            <span className="text-[13.5px] text-text-muted">
              <b className="text-text">{fileName}</b>: {entries.length} rows with an email
              {withoutId ? `, ${withoutId} without an ID` : ""}
            </span>
          ) : null}
        </div>

        {entries.length ? (
          <div className="mt-4">
            <DataTable<Entry>
              rows={entries.slice(0, 200)}
              rowKey={(e) => e.email}
              empty=""
              columns={[
                {
                  key: "email",
                  header: "Email",
                  sortValue: (e) => e.email,
                  render: (e) => e.email,
                },
                {
                  key: "id",
                  header: "Participant ID",
                  sortValue: (e) => e.participantId,
                  render: (e) => e.participantId ?? <span className="text-text-muted">Any ID</span>,
                },
              ]}
            />
            {entries.length > 200 ? (
              <p className="mt-2 text-[12.5px] text-text-muted">
                Showing the first 200 of {entries.length}.
              </p>
            ) : null}
            <label className="mt-4 flex items-center gap-2 text-[14px]">
              <input type="checkbox" checked={replace} onChange={() => setReplace(!replace)} />
              Replace the whole roster (remove anyone not in this file)
            </label>
            <button
              type="button"
              disabled={busy}
              onClick={() => onImport(entries, replace)}
              className="mt-3 h-10 rounded-[10px] bg-accent px-5 text-[14px] font-bold text-on-accent disabled:opacity-50"
            >
              {busy ? "Loading..." : `Load ${entries.length} entries`}
            </button>
          </div>
        ) : null}
        {message ? <Banner tone="good">{message}</Banner> : null}
        {error ? <Banner tone="bad">{error}</Banner> : null}
      </div>
    </div>
  );
}
